import { useMemo, useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Search, Pause, Play, Terminal } from "lucide-react";
import { Input } from "@/components/ui/input";

const LEVELS = {
  INFO: "text-info bg-info/10 border-info/25",
  WARN: "text-warn bg-warn/10 border-warn/25",
  ERROR: "text-err bg-err/10 border-err/25",
  DEBUG: "text-muted-foreground bg-white/5 border-line",
};

// Console de logs estilo terminal, com filtros de nível, busca e autoscroll.
export const LogViewer = ({ logs = [], height = "h-80", showFilters = true, live = false, title = "Console" }) => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState({ INFO: true, WARN: true, ERROR: true, DEBUG: true });
  const [autoscroll, setAutoscroll] = useState(true);
  const scrollRef = useRef(null);

  const filtered = useMemo(
    () =>
      logs.filter(
        (l) => active[l.level] && (!query || `${l.source} ${l.msg}`.toLowerCase().includes(query.toLowerCase())),
      ),
    [logs, active, query],
  );

  useEffect(() => {
    if (autoscroll && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [filtered, autoscroll]);

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-[#06070A]" data-testid="log-viewer">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-black/30 px-3 py-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Terminal className="h-3.5 w-3.5 text-brand/80" />
          {title}
          {live && (
            <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-ok/30 bg-ok/10 px-1.5 py-0.5 text-[10px] font-medium text-ok">
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-ok" /> LIVE
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {showFilters && (
            <>
              <div className="flex items-center gap-1">
                {Object.keys(LEVELS).map((lvl) => (
                  <button
                    key={lvl}
                    data-testid={`log-filter-${lvl}`}
                    onClick={() => setActive((a) => ({ ...a, [lvl]: !a[lvl] }))}
                    className={cn(
                      "rounded-md border px-1.5 py-0.5 text-[10px] font-semibold transition-opacity",
                      LEVELS[lvl],
                      !active[lvl] && "opacity-30",
                    )}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
              <div className="relative hidden sm:block">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filtrar..."
                  data-testid="log-search"
                  className="h-7 w-40 border-line bg-black/40 pl-7 text-xs"
                />
              </div>
            </>
          )}
          <button
            data-testid="log-autoscroll-toggle"
            onClick={() => setAutoscroll((v) => !v)}
            className={cn("flex h-7 w-7 items-center justify-center rounded-md border border-line text-muted-foreground transition-colors hover:text-foreground", autoscroll && "text-ok")}
            title={autoscroll ? "Autoscroll ligado" : "Autoscroll pausado"}
          >
            {autoscroll ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div ref={scrollRef} className={cn("overflow-auto p-3 font-mono text-xs leading-6", height)}>
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">Nenhuma linha corresponde aos filtros.</p>
        ) : (
          filtered.map((l, i) => (
            <div key={i} className="flex items-start gap-2 whitespace-nowrap py-px hover:bg-white/[0.02]" title={l.msg}>
              <span className="shrink-0 text-muted-foreground/70">{l.ts}</span>
              <span className={cn("shrink-0 rounded border px-1 text-[10px] font-semibold leading-4", LEVELS[l.level])}>{l.level}</span>
              {l.source && <span className="shrink-0 text-brand/70">{l.source}</span>}
              {l.server && l.server !== "—" && <span className="shrink-0 text-info/70">{l.server}</span>}
              <span className="shrink-0 text-foreground/85">{l.msg}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
